import { computed, makeObservable } from 'mobx';
import {Check} from '@sparkware/app-utils';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {IRunningGameHandler} from './running-game-handler.interface';
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {
  OpenGameIntermediatePageComponent
} from "../../components/lobby-game-info-preview/intermediate-page/open-game-intermediate-page.component";
import React from "react";
import {GameIconAnimationState} from '../../components/single-game/common/parts/game-image/game-image.component';
import {
  IRunningGameControllerViewModel
} from "../game-runner/interfaces/game-controller/running-game-controller.interface";
import {GameModeEnum} from "../game-runner/interfaces/game-context/game-context-view-model.interface";
import {IOpenFreePlayGameParams} from "../game-runner/game-runner.service.interface";
import {IGameRestriction} from "../restrictions/game-restriction.interface";
import {GAME_GROUP} from "../../../common/const/game_groups_consts";
import {NullableNumber, NullableString} from "../../../common/types/nullable-types";
import {GAMES_CATEGORIES} from "./games-categories.enum";
import {IGameInfo} from '../../../common/const/game-info.interface';
import {GAME_VOLATILITY} from '../../../sdk/src/services/games_information/games_information_service_interface';
import {
  OrbitMarketingEventAnalyticsCategory,
  OrbitMarketingEventAnalyticsLabel
} from "../analytics/analytics.service.interface";
import { GameChildishIconMode } from '../../../common/enums/game-childish-icon-mode.enum';
import {AreaType} from "@sparkware/uc-sdk-core";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../common/const/casinofied-analytics-consts";


export interface OpenGameParameters {
  isRealMode: boolean;
  pageName?: string;
  listTitle?: string;
  listPosition?: number;
  AdminListID?: number;
  marketingParams?: MarketingParams;
  gameIndex?: number;
  aiEngine?: string;
  aiSortingType?: string;
  AIListTypeID?: number;
  openFromDrawer?:boolean;
  showIntermediatePage?: boolean;
  isCalledFromIntermediatePage?: boolean;
  animatedIconState?: GameIconAnimationState;
  freePlayParams?: IOpenFreePlayGameParams;
  skipFreePlaySuggestion?: boolean;
  referrerId?: NullableNumber;
}



export function gameInfoComparerByTitle(game1: GameInfo, game2: GameInfo): number {
  if(game1.title < game2.title) {
    return -1
  } else if(game1.title > game2.title) {
    return 1
  }

  return 0;
}


export class GameInfo {
  constructor(private _rawGameData: IGameInfo,
              private services: IServiceFactoryExtended,
              private _aboutText: string) {
    makeObservable(this);

    Object.defineProperty(this._rawGameData, 'allowSwitchGameMode', {
      get: () => this.allowSwitchGameMode,
      configurable: true
    });
  }

  private get _isMobile(): boolean {
    return this.services.device.isMobile;
  }

  private get _isAuthorized(): boolean {
    return this.services.user.isAuthorized;
  }

  get gameType(): number {
    return this._rawGameData.gameType;
  }

  get title(): string {
    return this._rawGameData.title;
  }

  get numberOfLines(): number {
    return this._rawGameData.numberOfLines;
  }


  get hasJackpot(): boolean {
    return this._rawGameData.hasJackpot || false;
  }

  get isNewGame(): boolean {
    return this._hasCustomFilter('new');
  }

  get isExclusiveGame(): boolean {
    return this._hasCustomFilter('exclusive');
  }

  private _hasCustomFilter(filterName: string): boolean {
    if (!filterName) {
      return false;
    }

    if (!Check.isArray(this._rawGameData.filtersList)) {
      return false;
    }

    filterName = filterName.toLowerCase();

    return 0 <= this._rawGameData.filtersList.findIndex(filter => filter.toLowerCase() === filterName);

  }

  get hasBonusGames(): boolean {
    return this._rawGameData.hasBonusGames || false;
  }

  get numberOfDecks(): number {
    return this._rawGameData.numberOfDecks;
  }

  get isMultiHand(): boolean {
    return this._rawGameData.isMultiHand || false;
  }

  get hasFreeSpins(): boolean {
    return this._rawGameData.hasFreeSpins || false;
  }

  get subGroupName(): string {
    return this._rawGameData.subGroupName;
  }

  get isEliteLounge(): boolean {
    return this._rawGameData.isEliteLounge || false;
  }

  get isChildish(): boolean {
    return this._rawGameData.isChildish || false;
  }

  get hasSideBets(): boolean {
    return this._rawGameData.hasSideBets || false;
  }

  get maxBetPerSpot(): number {
    return this._rawGameData.maxBetPerSpot;
  }

  get allowWithdrawOnlyFromReal(): boolean {
    return this._rawGameData.allowWithdrawOnlyFromReal;
  }

  get hasLowStakes(): boolean {
    return this._hasCustomFilter('lowStakes');
  }

  get hasHighStakes(): boolean {
    return this._hasCustomFilter('highStakes');
  }

  get ClassicSlots(): boolean {
    return this._rawGameData.ClassicSlots;
  }

  get allowMinMaxBet(): boolean {
    let isAllowedCategory = this.isCardOrTableGame || this.isLiveCasino;

    return !this.hasJackpot && isAllowedCategory;
  }


  @computed
  get allowHoverDetails(): boolean {

    if (this.gameGroup === 'Live Casino') {
      return false;
    }

    return !this._isAuthorized || this._rawGameData.forceDemoButton;
  }

  get allowTap(): boolean {
    return this._isMobile;
  }


  async hasInfoPage(): Promise<boolean> {
    return Boolean(await this.getInfoPageURL());
  }

  @computed
  get allowDemoButtonInSingleGame(): boolean {

    if (!this._rawGameData.isAnonymousSupported) {
      return false;
    }

    if (this._rawGameData.forceDemoButton) {
      return true;
    }


    if (this._isMobile) {
      return false;
    }


    return (this._rawGameData.demoSettings
        && this._rawGameData.demoSettings.demoCTAAnonymous
        && this._rawGameData.demoSettings.demoCTAAnonymous.pc);
  }


  @computed
  get allowDemoButtonInIntermediatePage(): boolean {
    if (!this._rawGameData.isAnonymousSupported) {
      return false;
    }

    if (this._rawGameData.forceDemoButton) {
      return true;
    }

    if (this.services.user.isAuthorized) {
      return this._rawGameData.demoSettings && this._rawGameData.demoSettings.demoCTAAuthorized && this._rawGameData.demoSettings.demoCTAAuthorized.mobile;
    }

    return this._rawGameData.demoSettings && this._rawGameData.demoSettings.demoCTAAnonymous && this._rawGameData.demoSettings.demoCTAAnonymous.mobile;
  }

   async getInfoPageURL(): Promise<string> {

    if (!this._rawGameData.infoPageURL) {
      return '';
    }
    const siteMap = await this.services.siteMap;

    if (!siteMap.isUrlInSiteMap(this._rawGameData.infoPageURL)) {
      this.services.logger.debug(`Page info '${this._rawGameData.infoPageURL}' for game ${this.gameType} - ${this.title} is not in the site map`);
      return '';
    }

    return this._rawGameData.infoPageURL
  }

  //Can be either a live stream URL or a static video URL.
  get videoURL(): string {
    if (!this._rawGameData.videoURL) {
      return '';
    }

    return this._rawGameData.videoURL;
  }

  get hasVideoURL(): boolean {
    return Boolean(this.videoURL);
  }

  get gameGroup(): string {
    return this._rawGameData.gameGroup;
  }

  get gameGroupId(): GAME_GROUP {
    return this._rawGameData.gameGroupId;
  }

  get gameCategory(): string {
    if(this.isVideoSlot || this.isScratchCards) {
      return GAMES_CATEGORIES.SLOTS;
    }

    if(this.isLiveCasino) {
      return GAMES_CATEGORIES.LIVE;
    }

    if(this.isCardOrTableGame) {
      return GAMES_CATEGORIES.CARD_AND_TABLE;
    }

    return '';

  }

  get gameCategoryClassName(): string {
    return 'game-category-' + this.gameCategory;
  }

  get companyName(): string {
    return this._rawGameData.companyName;
  }

  get vendorId(): number {
    return this._rawGameData.vendorId;
  }

  get vendorName(): string {
    return this._rawGameData.vendorName;
  }

  private _getDefaultGameImage(opts: {squareIcon: boolean; animatedIcon: boolean;}): string {
      let images = this._rawGameData?.images;
      if (!images) {
          return '';
      }

      if (opts.squareIcon && images.hybrid_icon) {
          if (opts.animatedIcon) {
              return (images.animated_square) ? this.getAnimatedJsonUrl(this._getResolvedGameImagePath(images.animated_square)) : '';
          }
          return this._getResolvedGameImagePath(images.hybrid_icon);
      }

      if (opts.animatedIcon) {
          return (images.animated_round) ? this.getAnimatedJsonUrl(this._getResolvedGameImagePath(images.animated_round)) : '';
      }

      return this._getResolvedGameImagePath(images.icon);
  }

  private _shouldUseLessNonChildishIcon(gameChildishMode: GameChildishIconMode): boolean {
        return (gameChildishMode === GameChildishIconMode.UseLessNonChildishIcon || gameChildishMode === GameChildishIconMode.PreferLessNonChildishIcon)
  }

  private _getImageForChildishGame(opts: {squareIcon: boolean; animatedIcon: boolean;}): string {
      let images = this._rawGameData?.images;
      if (!images) {
          return '';
      }

      const gameChildishMode = this.services.regulation.current.getChildishIconMode();
      if (gameChildishMode === GameChildishIconMode.UseDefaultIcon) {
          return this._getDefaultGameImage(opts);
      }
      else {
          if (opts.squareIcon && images.hybrid_icon) {
              if (images.hybrid_less_non_childish_icon && this._shouldUseLessNonChildishIcon(gameChildishMode)) {
                  return opts.animatedIcon ? '' : this._getResolvedGameImagePath(images.hybrid_less_non_childish_icon);
              }
              else if (images.hybrid_non_childish_icon) {
                  /* we are happy with the - hybrid_non_childish_icon - for UseLessNonChildishIcon or UseNonChildishIcon MODE */
                  return opts.animatedIcon ? '' : this._getResolvedGameImagePath(images.hybrid_non_childish_icon);
              }
              else if (gameChildishMode === GameChildishIconMode.PreferNonChildishIcon || gameChildishMode === GameChildishIconMode.PreferLessNonChildishIcon) {
                  return this._getDefaultGameImage(opts);
              }
              else {
                  /* Handling scenario of mode: UseNonChildishIcon / UseLessNonChildishIcon, having - non_childish_icon / less_non_childish_icon - (game is not restricted) but missing hybrid_non_childish_icon / hybrid_less_non_childish_icon */
                  return opts.animatedIcon ? '' : this.getGameImagePlaceHolderUrl(opts.squareIcon);
              }
          }
          else {
              if (images.less_non_childish_icon && this._shouldUseLessNonChildishIcon(gameChildishMode)) {
                  return opts.animatedIcon ? '' : this._getResolvedGameImagePath(images.less_non_childish_icon);
              }
              else if (images.non_childish_icon) {
                  /* we are happy with the - non_childish_icon - for UseLessNonChildishIcon or UseNonChildishIcon MODE */
                  return opts.animatedIcon ? '' : this._getResolvedGameImagePath(images.non_childish_icon);
              }
              else {
                  /* game is Childish & does not have - non_childish_icon or less_non_childish_icon - & is not restricted, it means we just PreferNonChildishIcon and we are happy with default icon*/
                  return this._getDefaultGameImage(opts);
              }
          }
      }
  }

  getGameImage(options?: {squareIcon?: boolean; animatedIcon?: boolean;}): string {
      let opts = {
          squareIcon: options?.squareIcon || false,
          animatedIcon: options?.animatedIcon || false,
      }
      let images = this._rawGameData && this._rawGameData.images;
      if (!images) {
          return '';
      }

      if(this.isChildish){
        return this._getImageForChildishGame(opts);
      }
      else {
        return this._getDefaultGameImage(opts);
      }
  }

  getBackgroundImage(): string {
    let background = this._rawGameData?.images?.background;
    if (!background) {
      return '';
    }

    return this._getResolvedGameImagePath(background);
  }

  private getAnimatedJsonUrl(pngImagePath: string) {
    if (!pngImagePath.endsWith('.png')) {
      this.services.logger.error("Animated icon path does not end in '.png': " + pngImagePath);
      return '';
    }
    // we don't expect for substring '.png' to exist in other place than the end of the string, so we do a simple replace
    return pngImagePath.replace('.png', '.json');
  }

  getAnimatedImageTimings() {
    const animatedImageTimings = this._rawGameData && this._rawGameData.animatedImageTimings
    return {
      delay: animatedImageTimings?.delay || 2000,
      interval: animatedImageTimings?.interval || 3000,
    }
  }

  get gameImageInsideIntermediatePage(): string {
    const icon = this._rawGameData?.images?.icon;
    if (!icon) {
      return '';
    }

    return this._getResolvedGameImagePath(icon);
  }

  get gameImageInHistoryPage(): string {
    let images = this._rawGameData && this._rawGameData.images;
    if (!images) {
      return '';
    }

    let image: string = images.icon;
    if (this.services.theme.shouldUseSquareIcons && images.hybrid_icon) {
      image = images.hybrid_icon;
    }

    return this._getResolvedGameImagePath(image);
  }

  get badge(): string | null {
    return this._rawGameData.badge;
  }

  get videoThumbnailUrl(): string {
    const video_thumbnail = this._rawGameData?.images?.video_thumbnail;
    if (!video_thumbnail) {
      return '';
    }

    return this._getResolvedGameImagePath(video_thumbnail);
  }

  get slideCover() : string {
    const slideCover = this._rawGameData?.images?.slide_cover;
    if (!slideCover) {
      return '';
    }

    return this._getResolvedGameImagePath(slideCover);
  }

  get slideVideo() : string {
    const slideVideo = this._rawGameData?.images?.slide_video;
    if (!slideVideo) {
      return '';
    }

    return this._getResolvedGameImagePath(slideVideo);
  }

  @computed
  get badgeUrl(): string | null {
    if (this.badge) {
      return this.services.configuration.data.generalSettings.gamesSettings.gameBadgePath + this.badge + '.png';
    }

    return null;
  }

  get minBet(): number {
    return this._rawGameData.minBet;
  }

  get maxBet(): number {
    return this._rawGameData.maxBet;
  }

  get maxBetPerBrand(): number {
    return this._rawGameData.maxBetPerBrand;
  }

  getGameImagePlaceHolderUrl(useSquareIcon?: boolean): string {
    if (useSquareIcon) {
      return this.services.theme.currentTheme.images.gameIconPlaceHolderSq.toString();
    }
    return this.services.theme.currentTheme.images.gameIconPlaceHolder.toString();
  }

  getImageSeoAlt() {

    let seoValue = this.services.configuration.data?.seo?.games?.images?.alt;

    return this.services.translation.translate(seoValue, {
      gameName: this.title,
      categoryName: this.gameCategory
    });
  };

  private _getResolvedGameImagePath(imagePath: string): string {
    return this.services.configuration.resolveGameImagePath(imagePath);
  }

  @computed
  get stripImage(): string {
    let images = this._rawGameData.images;
    if (!images) {
      return '';
    }

    return this._isMobile ? this._getResolvedGameImagePath(images.strip_mobile) :
        this._getResolvedGameImagePath(images.strip_pc);
  }

  @computed
  get stripeImageFallback(): string {
    let generalSettings = this.services.configuration.data.generalSettings;
    let previewDefaultImage = generalSettings && generalSettings.gamesSettings && generalSettings.gamesSettings.gamesPreviewDefaultImages;

    if (previewDefaultImage && this.gameCategory) {
      //Replace the game category dash to under score (like the image name for example card-and-table category name should be default_game_strip_card_and_table)
      let gameCategoryWithUnderScores = this.gameCategory.replace(/-/g, '_');
      previewDefaultImage = previewDefaultImage.replace(/{arena-category}/g, gameCategoryWithUnderScores);
      //Add _mobile if we are in mobile

      if (this.services.device.isMobile) {
        return previewDefaultImage.replace('.png', '_mobile.png');
      }

      return previewDefaultImage;
    }

    return ''
  }

  get hasVideoTrailer(): boolean {
    return this.gameType === 130136;
  }

  get aboutText(): string {
    return this._aboutText;
  }

  get multiplayer(): number {
    return this._rawGameData.multiplayer;
  }

  get isClassicSlot(): boolean {
    return this._hasCustomFilter('ClassicSlots');
  }

  get hasHighVolatility(): boolean {
    return this.volatility === GAME_VOLATILITY.High;
  }

  get hasLowVolatility(): boolean {
    return this.volatility === GAME_VOLATILITY.Low;
  }

  get hasMediumVolatility(): boolean {
    return this.volatility === GAME_VOLATILITY.Medium;
  }

  get hasDailyJackpot(): boolean {
    return this._hasCustomFilter('DailyJackpot');
  }

  get hasTwilightJackpot(): boolean {
    return this._hasCustomFilter('TwilightJackpot');
  }

  get hasCaptain8tJackpot(): boolean {
    return this._hasCustomFilter('Captain8Jackpot');
  }

  get hasRedTigerJackpot(): boolean {
    return this._hasCustomFilter('RedTigerJackpot');
  }

  @computed
  get shouldShowIntermediatePage(): boolean {
    if (!this._isMobile) {
      return false;
    }

    if (!this._rawGameData.isAnonymousSupported) {
      return false;
    }

    if (this.isLiveCasino) {
      return false;
    }

    if (this._rawGameData.forceDemoButton) {
      return true;
    }

    let demoSettings = this._rawGameData.demoSettings;
    if (this._isAuthorized) {
      if (demoSettings?.intermediatePageAuthorized?.mobile) {
        return true;
      }
    } else {
      if (demoSettings?.intermediatePageAnonymous?.mobile) {
        return true;
      }
    }

    return false;
  }

  get isDemoModeSupported(): boolean {
    return Boolean(this._rawGameData.isAnonymousSupported);
  }

  @computed
  get allowSwitchGameMode(): boolean {

    if(!this.isDemoModeSupported) {
      return false;
    }

    let demoSettings = this._rawGameData.demoSettings;
    if (!demoSettings) {
      return false;
    }

    if (this._isAuthorized) {
      return Boolean(demoSettings?.demoCTAAuthorized?.pc);
    } else {
      return Boolean(demoSettings?.demoCTAAnonymous?.pc);
    }
  }

  async open(params: OpenGameParameters): Promise<IRunningGameHandler> {

    if(this._showIntermediatePage(params)) {
      return;
    }

    if (await this._shouldRedirectToMembership(params)) {
      this._openMembership();
      return {
        closeGame: () => {
        }
      };
    }

    this._sendOpenGameAnalytics(params);

    let gameController: IRunningGameControllerViewModel | null = null;
    if (params.isRealMode) {
      if(params.marketingParams) {
        this.services.sdk.eventsTrigger.updateMarketingParams(params.marketingParams);
      }
      gameController = await (await this.services.gameRunner).openGame({
        gameMode: GameModeEnum.Real,
        gameInfo: this,
        freePlayParams: params.freePlayParams,
        skipFreePlaySuggestion: params.skipFreePlaySuggestion,
        referrerId: params.referrerId
      });
    } else {
      gameController = await (await this.services.gameRunner).openGame({
        gameMode: GameModeEnum.Demo,
        gameInfo: this
      });
    }

    return {
      closeGame: async () => {
        await gameController?.closeGame({
          shouldSuggestNextGame: false
        })
      }
    }

  }

  private async _shouldRedirectToMembership(params: OpenGameParameters): Promise<boolean> {
    return params.isRealMode && params.isCalledFromIntermediatePage
        && !this.services.user.isAuthorized
        && ((await this.services.cmsSegmentation).isCstatus === false);
  }


  //TODO - this should return a Promise
  private _showIntermediatePage(params: OpenGameParameters): boolean {
    if (this.shouldShowIntermediatePage && params.showIntermediatePage) {
      const dialogContentRenderer = ((dialogHandler) => <OpenGameIntermediatePageComponent gameInfo={this}
                                                                                           openGameParams={params}
                                                                                           dialogHandler={dialogHandler}/>);
      this.services.dialog.showModal(dialogContentRenderer, {hideCloseButton: true});

      return true;
    }

    return false;
  }


  private _openMembership() {
    this.services.analytics.sendOrbitMarketingEvent(OrbitMarketingEventAnalyticsCategory.Conversion, 'Play', OrbitMarketingEventAnalyticsLabel.PreGamePopUp);
    this.services.externalLinks.then(externalLinks => externalLinks.openMembership({
      channel: {
        area: AreaType.InGame,
        source: CHANNEL_SOURCE.casinoClient,
        element: CHANNEL_ELEMENTS.registerButton
      },
      marketingParams: {
        dl: "20614"
      }
    }));
  }

  private _sendOpenGameAnalytics(params: OpenGameParameters): void {
    if (!params.pageName || !params.listTitle) {
      return;
    }
    const listTitle = this._replaceUnderscore(params.listTitle);
    this.services.analytics.sendElasticInfoEvent({eventType:"CGP_Game_Click",category: params.pageName, action: 'game_click', label: listTitle, value: this.gameType.toString(), customData: params}, true);
  }


  isAllWordsMatch(words: RegExp[]) {
    let text = this.title + ' ' + this.vendorName;
    for (let index = 0; index < words.length; index++) {
      if (!text.match(words[index])) {
        return false;
      }
    }

    return true;
  }

  get gameArenaIcon(): string {
    const arena_icon = this._rawGameData?.images?.arena_icon;
    if (!arena_icon) {
      return '';
    }

    return this._getResolvedGameImagePath(arena_icon);
  }

  /**
   * @description BI/BA request not to send the underscore as part of the list title since they split by underscore to get all the data on the label
   * @param listTitle
   * @private
   */
  private _replaceUnderscore(listTitle): string {
    return listTitle.replace(/_/g, '-');
  }

  get restriction(): IGameRestriction {

    if(this._rawGameData.restriction) {
      return this._rawGameData.restriction as IGameRestriction;
    }

    return this.services.restrictions.getGameRestriction(this);
  }

  get isVideoSlot(): boolean {
     return this.gameGroupId === GAME_GROUP.VIDEO_SLOT
            || this.gameGroupId === GAME_GROUP.SLOT
  }

  //same as isCardOrTableGame() besides ARCADE, PAI_GOW_POKER, ROULETTE_LIKE
  get isCasinoGame() {
    const casinoGamesGroups = [
      GAME_GROUP.BACCARAT,
      GAME_GROUP.BLACKJACK,
      GAME_GROUP.CARRIBEAN_POKER,
      GAME_GROUP.CRAPS,
      GAME_GROUP.KENO,
      GAME_GROUP.POKER,
      GAME_GROUP.POKER_TEMPLATE_GAMES,
      GAME_GROUP.ROULETTE,
      GAME_GROUP.SCRATCH_CARD,
      GAME_GROUP.VIDEO_POKER
    ];

    return casinoGamesGroups.includes(this.gameGroupId);
  }

  get isVideoPoker(): boolean {
    return this.gameGroupId === GAME_GROUP.VIDEO_POKER;
  }

  get isScratchCards(): boolean {
    return this.gameGroupId === GAME_GROUP.SCRATCH_CARD;
  }

  get isBlackJack(): boolean {
    return this.gameGroupId === GAME_GROUP.BLACKJACK;
  }

  get isRoulette(): boolean {
    return this.gameGroupId === GAME_GROUP.ROULETTE;
  }

  get isLiveCasino(): boolean {
    return this.gameGroupId === GAME_GROUP.LIVE_CASINO;
  }

  get isBaccarat(): boolean {
    return this.gameGroupId === GAME_GROUP.BACCARAT;
  }


  get isCardOrTableGame(): boolean {

    return this.isBlackJack
        || this.isRoulette
        || this.isBaccarat
        || this.isVideoPoker
        || this.gameGroupId === GAME_GROUP.CRAPS
        || this.gameGroupId === GAME_GROUP.KENO
        || this.gameGroupId === GAME_GROUP.CARRIBEAN_POKER
        || this.gameGroupId === GAME_GROUP.POKER_TEMPLATE_GAMES
        || this.gameGroupId === GAME_GROUP.ROULETTE_LIKE
        || this.gameGroupId === GAME_GROUP.ARCADE
        || this.gameGroupId === GAME_GROUP.POWER_VIDEO_POKER
        || this.gameGroupId === GAME_GROUP.PAI_GOW_POKER
        || this.gameGroupId === GAME_GROUP.CRAZY_BLACKJACK
  }

  get externalGameCode(): NullableString {
    return this._rawGameData.externalGameCode;
  }

  get themeIds(): number[] {
    return this._rawGameData.gameThemes ?? [];
  }

  get volatility(): GAME_VOLATILITY {
    return this._rawGameData.volatility;
  }
}
